import { useSetToast } from "@app/components/Toast";
import { SupportedLocales } from "@app/shared/utils/locales";
import { Icon } from "@design-system/Icon";
import { Textfield } from "@design-system/Inputs/Textfield";
import { FlexCol, FlexRow } from "@design-system/Layout/Flex";
import { Modal } from "@design-system/Modal";
import { Text } from "@design-system/Typography/Text";
import { ResponseStatus } from "@generated/client/graphql";
import { useMemo, useState, type ChangeEvent, type FC } from "react";
import {
    useDeleteSupplierTemplateQuestionGroup,
    useTemplateQuestionGroups,
    useUpdateSupplierTemplateQuestionGroup,
} from "../../data";
import { useSupplierQuestionsGroupSelectTranslations } from "./i18n";
import { QuestionGroupDeleteModal } from "./QuestionGroupDeleteModal";

export const QuestionGroupEditModal: FC<{
    open: boolean;
    onOpenChange: (open: boolean) => void;
    questionGroup: {
        id: string;
        name: {
            en?: string;
            fr?: string;
        };
    };
    availableLocales: SupportedLocales[];
}> = ({ open, onOpenChange, questionGroup, availableLocales }) => {
    const { templateQuestionGroups } = useTemplateQuestionGroups();

    const { t, keys } = useSupplierQuestionsGroupSelectTranslations();

    const { updateQuestionGroup, isPending: isUpdatePending } =
        useUpdateSupplierTemplateQuestionGroup();

    const { deleteQuestionGroup, isPending: isDeletePending } =
        useDeleteSupplierTemplateQuestionGroup();

    const { setToastError } = useSetToast();

    const [englishName, setEnglishName] = useState<string | undefined>(
        questionGroup.name.en,
    );
    const [frenchName, setFrenchName] = useState<string | undefined>(
        questionGroup.name.fr,
    );

    const [isDeleteQuestionGroupModalOpen, setIsDeleteQuestionGroupModalOpen] =
        useState(false);

    const handleEnglishNameChange = (e: ChangeEvent<HTMLInputElement>) => {
        setEnglishName(e.target.value);
    };
    const handleFrenchNameChange = (e: ChangeEvent<HTMLInputElement>) => {
        setFrenchName(e.target.value);
    };

    const handleOpenChange = (open: boolean) => {
        setEnglishName(undefined);
        setFrenchName(undefined);
        onOpenChange(open);
    };

    const showEnglishName = useMemo(() => {
        return availableLocales.includes(SupportedLocales.EN);
    }, [availableLocales]);

    const showFrenchName = useMemo(() => {
        return availableLocales.includes(SupportedLocales.FR);
    }, [availableLocales]);

    const isEnglishNameUnique = useMemo(() => {
        if (
            questionGroup.name.en?.toLowerCase() === englishName?.toLowerCase()
        ) {
            return true;
        }
        if (!englishName) {
            return true;
        }
        return !templateQuestionGroups.some(
            (group) =>
                group.name?.en?.toLowerCase() === englishName?.toLowerCase(),
        );
    }, [templateQuestionGroups, questionGroup.name.en, englishName]);

    const isFrenchNameUnique = useMemo(() => {
        if (
            questionGroup.name.fr?.toLowerCase() === frenchName?.toLowerCase()
        ) {
            return true;
        }
        if (!frenchName) {
            return true;
        }
        return !templateQuestionGroups.some(
            (group) =>
                group.name?.fr?.toLowerCase() === frenchName?.toLowerCase(),
        );
    }, [templateQuestionGroups, questionGroup.name.fr, frenchName]);

    const englishNameError = useMemo(() => {
        if (!showEnglishName) {
            return undefined;
        }
        if (!isEnglishNameUnique) {
            return t(keys.modal_name_already_exists);
        }
        if (englishName?.trim() === "") {
            return t(keys.modal_name_required);
        }
        return undefined;
    }, [englishName, t, keys, isEnglishNameUnique, showEnglishName]);

    const frenchNameError = useMemo(() => {
        if (!showFrenchName) {
            return undefined;
        }
        if (!isFrenchNameUnique) {
            return t(keys.modal_name_already_exists);
        }
        if (frenchName?.trim() === "") {
            return t(keys.modal_name_required);
        }
        return undefined;
    }, [frenchName, t, keys, isFrenchNameUnique, showFrenchName]);

    const handleDelete = () => {
        setIsDeleteQuestionGroupModalOpen(true);
    };

    const handleDeleteConfirm = () => {
        deleteQuestionGroup(questionGroup.id, {
            onSettled(data) {
                if (
                    data?.deleteSupplierTemplateQuestionGroup?.status ===
                    ResponseStatus.Success
                ) {
                    setIsDeleteQuestionGroupModalOpen(false);
                    onOpenChange(false);
                    return;
                }
                setToastError(t(keys.modal_toast_error_delete_question_group));
            },
        });
    };

    const handleSave = () => {
        if (!!englishNameError || !!frenchNameError) {
            return;
        }

        updateQuestionGroup(
            {
                id: questionGroup.id,
                nameMultiLocale: {
                    en: englishName,
                    fr: frenchName,
                },
            },
            {
                onSettled(data) {
                    if (
                        data?.updateQuestionGroup?.status ===
                        ResponseStatus.Success
                    ) {
                        onOpenChange(false);
                        return;
                    }
                    setToastError(
                        t(keys.modal_toast_error_edit_question_group),
                    );
                },
            },
        );
    };

    return (
        <>
            <Modal open={open} onOpenChange={handleOpenChange}>
                <Modal.Overlay centerContent scrollable>
                    <Modal.Content className="max-w-[600px]">
                        <Modal.Header>
                            <Modal.Title>
                                {t(keys.modal_edit_title)}
                            </Modal.Title>
                        </Modal.Header>
                        <Modal.Body>
                            <FlexCol gap="6">
                                <FlexCol gap="4" className="max-w-[400px]">
                                    {showEnglishName && (
                                        <Textfield
                                            value={englishName}
                                            onChange={handleEnglishNameChange}
                                            placeholder={t(
                                                keys.modal_english_name,
                                            )}
                                            error={englishNameError}
                                            required
                                        />
                                    )}
                                    {showFrenchName && (
                                        <Textfield
                                            value={frenchName}
                                            onChange={handleFrenchNameChange}
                                            placeholder={t(
                                                keys.modal_french_name,
                                            )}
                                            error={frenchNameError}
                                            required
                                        />
                                    )}
                                </FlexCol>
                                <FlexRow
                                    className="cursor-pointer"
                                    onClick={handleDelete}
                                    alignItems="center"
                                    gap="4"
                                >
                                    <Icon
                                        name="trash"
                                        className="text-error-500"
                                        size="md"
                                    />
                                    <FlexCol alignItems="start">
                                        <Text
                                            variant="body-bold"
                                            color="primary"
                                        >
                                            {t(keys.modal_delete_group_label)}
                                        </Text>
                                        <Text variant="body" color="primary">
                                            {t(
                                                keys.delete_group_label_description,
                                            )}
                                        </Text>
                                    </FlexCol>
                                </FlexRow>
                            </FlexCol>
                        </Modal.Body>
                        <Modal.Footer>
                            <Modal.CancelCTA>
                                {t(keys.modal_cancel)}
                            </Modal.CancelCTA>
                            <Modal.ConfirmCTA
                                preventClose
                                onClick={handleSave}
                                loading={isUpdatePending}
                            >
                                {t(keys.modal_save)}
                            </Modal.ConfirmCTA>
                        </Modal.Footer>
                    </Modal.Content>
                </Modal.Overlay>
            </Modal>
            <QuestionGroupDeleteModal
                open={isDeleteQuestionGroupModalOpen}
                onOpenChange={setIsDeleteQuestionGroupModalOpen}
                loading={isDeletePending}
                onDelete={handleDeleteConfirm}
            />
        </>
    );
};
